/**
 * Song overview (W2-08 timing; DESIGN.md §4.15 "SONG OVERVIEW", §5.2 "First beat"): the whole song as a
 * horizontal waveform with the beat grid of the chart drawn over it.
 *
 *  - Click a downbeat: beat 0 moves there (snapped to the strongest attack within 40 ms; Alt+click does not snap).
 *  - Drag the grid sideways: beat 0 follows live (one undoable edit on release; Escape cancels).
 *  - The strip at the top seeks (click or drag), so moving around never moves the grid by accident.
 *  - Ctrl+wheel zooms at the pointer, the wheel scrolls a zoomed view; the buttons zoom and show the whole song;
 *    with keyboard focus: Left / Right scroll, + / - zoom, 0 shows the whole song.
 *  - Bar lines are stronger than beat lines; beat 0 has its own marker; the playhead moves while playing.
 * Times are music times as the browser decodes the file (`shiftSec` = where beat 0 plays). Shared by the Song &
 * Timing workspace and the wizard Beat step.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { peakRange, pickPeakLevel } from '../../core/audio-dsp/peaks';
import type { OnsetEnvelope, PeakLevel } from '../../core/audio-dsp/types';
import { formatSec } from '../../core/time/beatSyntax';
import { Beats } from '../../core/time/beat';
import type { MeterMap } from '../../core/time/meter';
import type { TempoMap } from '../../core/time/types';
import type { FrameScheduler } from '../../render/types';
import { IconButton } from '../../ui';
import { rulerStep, useCanvasView, vline, type CanvasFrame } from './canvasView';
import { m } from './timing.messages';
import type { TimingText } from './timingHooks';
import { beatF64AtChartSec, chartSecAtBeatF64, snapToOnset } from './timingModel';
import s from './Timing.module.css';

/** Height of the seek strip (CSS px). */
export const RULER_HEIGHT = 16;
/** Movement (px) that turns a press on the grid into a drag. */
const DRAG_THRESHOLD = 3;
/** Smallest visible span (s). */
const MIN_SPAN = 0.25;

export interface OverviewView {
  readonly start: number;
  readonly span: number;
}

export interface SongOverviewProps {
  readonly text: TimingText;
  readonly scheduler: FrameScheduler;
  /** Scheduler id (unique per mounted overview). */
  readonly id: string;
  readonly peaks: readonly PeakLevel[] | null;
  readonly onsets: OnsetEnvelope | null;
  readonly sampleRate: number | null;
  readonly durationSec: number;
  readonly tempo: TempoMap;
  readonly meter: MeterMap;
  /** Music time (s, decoded) of beat 0. */
  readonly shiftSec: number;
  /** Music time of the playhead now (null: none). */
  playheadMusicSec(): number | null;
  readonly playing: boolean;
  onSeek(musicSec: number): void;
  onBeatZero(musicSec: number): void;
  /** Grid drag: the grid moved by `deltaSec` (move), was released (end) or cancelled (cancel). */
  onDrag(deltaSec: number, phase: 'move' | 'end' | 'cancel'): void;
  /** Pointer music time while hovering (null when it leaves): the `timing.beatZeroHere` key. */
  readonly onHover?: ((musicSec: number | null) => void) | undefined;
  readonly label?: string | undefined;
}

/** View clamped to the song (span between MIN_SPAN and the song length). */
export function clampView(v: OverviewView, duration: number): OverviewView {
  const total = Math.max(MIN_SPAN, duration);
  const span = Math.max(MIN_SPAN, Math.min(total, v.span));
  const start = Math.max(0, Math.min(total - span, v.start));
  return { start, span };
}

/** Zooms the view by `factor` (< 1 zooms in) keeping the time at fraction `at` (0..1) of the width in place. */
export function zoomView(v: OverviewView, factor: number, at: number, duration: number): OverviewView {
  const t = v.start + at * v.span;
  const span = v.span * factor;
  return clampView({ start: t - at * span, span }, duration);
}

interface DrawData {
  readonly peaks: readonly PeakLevel[] | null;
  readonly sampleRate: number | null;
  readonly view: OverviewView;
  readonly tempo: TempoMap;
  readonly meter: MeterMap;
  readonly shiftSec: number;
  readonly playhead: number | null;
  readonly hover: number | null;
  readonly beatZeroLabel: string;
}

function drawOverview(f: CanvasFrame, d: DrawData): void {
  const { ctx, width: w, height: h, theme } = f;
  const { start, span } = d.view;
  const xOf = (sec: number): number => ((sec - start) / span) * w;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = theme.overviewBackground;
  ctx.fillRect(0, 0, w, h);
  // Seek strip with time labels.
  ctx.fillStyle = theme.gutterBackground;
  ctx.fillRect(0, 0, w, RULER_HEIGHT);
  const step = rulerStep(span, w);
  ctx.strokeStyle = theme.divider;
  ctx.fillStyle = theme.textMuted;
  ctx.font = `10px ${theme.fontMono}`;
  ctx.textBaseline = 'middle';
  for (let t = Math.ceil(start / step) * step; t <= start + span; t += step) {
    const x = xOf(t);
    vline(ctx, x, RULER_HEIGHT - 4, RULER_HEIGHT);
    ctx.fillText(formatSec(t).replace(/\.000$/, ''), x + 3, RULER_HEIGHT / 2);
  }
  // Waveform.
  const top = RULER_HEIGHT;
  const mid = top + (h - top) / 2;
  const amp = (h - top) / 2 - 2;
  if (d.peaks !== null && d.sampleRate !== null && d.sampleRate > 0) {
    const samplesPerPx = (span / w) * d.sampleRate;
    const level = pickPeakLevel(d.peaks, samplesPerPx);
    if (level !== null) {
      ctx.fillStyle = theme.waveform;
      for (let x = 0; x < w; x++) {
        const t0 = start + (x / w) * span;
        const r = peakRange(level, t0 * d.sampleRate, (t0 + span / w) * d.sampleRate);
        if (r === null) continue;
        const y0 = mid - Math.max(0, r.max) * amp;
        const y1 = mid - Math.min(0, r.min) * amp;
        ctx.fillRect(x, y0, 1, Math.max(1, y1 - y0));
      }
    }
  }
  // Beat grid (beat lines only when they are at least 4 px apart; bar lines always, thinned when crowded).
  const b0 = Math.floor(beatF64AtChartSec(d.tempo, start - d.shiftSec));
  const b1 = Math.ceil(beatF64AtChartSec(d.tempo, start + span - d.shiftSec));
  const beatPx = (span > 0 ? w / span : 0) * (60 / Math.max(1, d.tempo.bpmAtBeatF64(Math.max(0, b0))));
  if (beatPx >= 4 && b1 - b0 < 20000) {
    ctx.strokeStyle = theme.gridBeat;
    for (let b = b0; b <= b1; b++) {
      if (d.meter.isBarStart(Beats.fromInt(b))) continue;
      ctx.globalAlpha = b < 0 ? 0.4 : 1;
      vline(ctx, xOf(d.shiftSec + chartSecAtBeatF64(d.tempo, b)), top, h);
    }
    ctx.globalAlpha = 1;
  }
  const bars = barStartsOrNone(d.meter, b0, b1);
  const barEvery = Math.max(1, Math.ceil(6 / Math.max(1e-6, beatPx * (d.meter.beatsPerBarAt(Beats.ZERO) || 4))));
  ctx.strokeStyle = theme.gridBar;
  for (const [i, bar] of bars.entries()) {
    if (i % barEvery !== 0) continue;
    const beat = Beats.toF64(bar);
    ctx.globalAlpha = beat < 0 ? 0.4 : 1;
    vline(ctx, xOf(d.shiftSec + chartSecAtBeatF64(d.tempo, beat)), top, h, 1);
  }
  ctx.globalAlpha = 1;
  // Beat 0.
  const zx = xOf(d.shiftSec);
  if (zx >= -2 && zx <= w + 2) {
    ctx.strokeStyle = theme.accent;
    vline(ctx, zx, top, h, 2);
    ctx.fillStyle = theme.accent;
    ctx.font = `11px ${theme.fontSans}`;
    ctx.textBaseline = 'top';
    ctx.fillText(d.beatZeroLabel, zx + 4, top + 2);
  }
  if (d.hover !== null) {
    ctx.strokeStyle = theme.textMuted;
    ctx.setLineDash([3, 3]);
    vline(ctx, xOf(d.hover), 0, h);
    ctx.setLineDash([]);
  }
  if (d.playhead !== null) {
    ctx.strokeStyle = theme.playhead;
    vline(ctx, xOf(d.playhead), 0, h, 2);
  }
}

/** Bar starts between two whole beats (none when the range is outside the meter's range). */
function barStartsOrNone(meter: MeterMap, b0: number, b1: number): readonly ReturnType<MeterMap['barStart']>[] {
  try {
    return meter.barStartsBetween(Beats.fromInt(b0), Beats.fromInt(b1), 20000);
  } catch {
    return [];
  }
}

interface PressState {
  readonly mode: 'seek' | 'grid';
  readonly pointerId: number;
  readonly startX: number;
  dragging: boolean;
  lastDelta: number;
}

export function SongOverview(props: SongOverviewProps) {
  const { text, durationSec } = props;
  const [view, setView] = useState<OverviewView>({ start: 0, span: Math.max(MIN_SPAN, durationSec) });
  const [hover, setHover] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const press = useRef<PressState | null>(null);
  const fitted = useRef(durationSec);

  // Show the whole song again when its length becomes known or changes.
  useEffect(() => {
    if (fitted.current !== durationSec) {
      fitted.current = durationSec;
      setView({ start: 0, span: Math.max(MIN_SPAN, durationSec) });
    }
  }, [durationSec]);

  const v = clampView(view, durationSec);
  const draw = useCallback(
    (f: CanvasFrame) => {
      drawOverview(f, {
        peaks: props.peaks,
        sampleRate: props.sampleRate,
        view: v,
        tempo: props.tempo,
        meter: props.meter,
        shiftSec: props.shiftSec,
        playhead: props.playheadMusicSec(),
        hover,
        beatZeroLabel: text.t(m.overviewBeatZero),
      });
    },
    [props, v, hover, text],
  );
  const { canvasRef } = useCanvasView(props.scheduler, props.id, draw, props.playing);

  const secAt = (clientX: number): number => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) return v.start;
    return v.start + ((clientX - rect.left) / rect.width) * v.span;
  };

  // Wheel: Ctrl zooms at the pointer, otherwise scroll (native listener: React wheel handlers are passive).
  const viewRef = useRef(v);
  useLayoutEffect(() => {
    viewRef.current = v;
  });
  useEffect(() => {
    const el = hostRef.current;
    if (el === null) return undefined;
    const onWheel = (e: WheelEvent): void => {
      const rect = el.getBoundingClientRect();
      const at = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
      const cur = viewRef.current;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setView(zoomView(cur, e.deltaY > 0 ? 1.25 : 0.8, Math.max(0, Math.min(1, at)), durationSec));
        return;
      }
      if (cur.span >= durationSec - 1e-6) return;
      e.preventDefault();
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      setView(
        clampView({ start: cur.start + (delta / Math.max(1, rect.width)) * cur.span, span: cur.span }, durationSec),
      );
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [durationSec]);

  const deltaFor = (clientX: number, st: PressState): number => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const w = rect === undefined || rect.width <= 0 ? 1 : rect.width;
    return ((clientX - st.startX) / w) * v.span;
  };

  const endPress = (cancel: boolean): void => {
    const st = press.current;
    press.current = null;
    setDragging(false);
    if (st?.mode === 'grid' && st.dragging) props.onDrag(cancel ? 0 : st.lastDelta, cancel ? 'cancel' : 'end');
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape' && press.current !== null) {
      e.preventDefault();
      endPress(true);
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const dir = e.key === 'ArrowLeft' ? -1 : 1;
      setView(clampView({ start: v.start + dir * v.span * 0.1, span: v.span }, durationSec));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      setView(zoomView(v, 0.8, 0.5, durationSec));
    } else if (e.key === '-') {
      e.preventDefault();
      setView(zoomView(v, 1.25, 0.5, durationSec));
    } else if (e.key === '0') {
      e.preventDefault();
      setView({ start: 0, span: durationSec });
    }
  };

  const hoverText = useMemo(() => {
    if (hover === null) return null;
    const beat = beatF64AtChartSec(props.tempo, hover - props.shiftSec);
    return text.t(m.overviewHover, { time: formatSec(hover), beat: (Math.round(beat * 100) / 100).toFixed(2) });
  }, [hover, props.tempo, props.shiftSec, text]);

  const { onHover } = props;
  return (
    <div className={s.canvasBox}>
      <div className={s.toolbar}>
        <span className={s.toolbarTitle}>{props.label ?? text.t(m.panelMain)}</span>
        <span className={`${s.grow} ${s.subtle}`}>{text.t(m.overviewHelp)}</span>
        <IconButton
          icon="ZoomIn"
          size="sm"
          label={{ key: m.overviewZoomIn }}
          onClick={() => {
            setView(zoomView(v, 0.5, 0.5, durationSec));
          }}
        />
        <IconButton
          icon="ZoomOut"
          size="sm"
          label={{ key: m.overviewZoomOut }}
          onClick={() => {
            setView(zoomView(v, 2, 0.5, durationSec));
          }}
        />
        <IconButton
          icon="Maximize2"
          size="sm"
          label={{ key: m.overviewZoomFit }}
          onClick={() => {
            setView({ start: 0, span: durationSec });
          }}
        />
      </div>
      <div
        ref={hostRef}
        className={s.canvasHost}
        tabIndex={0}
        role="group"
        aria-label={text.t(m.overviewLabel)}
        aria-roledescription={text.t(m.overviewRoleDescription)}
        aria-busy={props.peaks === null}
        data-wheel="native"
        data-testid="timing-overview"
        onKeyDown={onKeyDown}
      >
        <canvas
          ref={canvasRef}
          className={s.canvas}
          data-cursor={dragging ? 'grabbing' : 'pointer'}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const mode = y < RULER_HEIGHT ? 'seek' : 'grid';
            press.current = { mode, pointerId: e.pointerId, startX: e.clientX, dragging: false, lastDelta: 0 };
            e.currentTarget.setPointerCapture(e.pointerId);
            if (mode === 'seek') props.onSeek(Math.max(0, secAt(e.clientX)));
          }}
          onPointerMove={(e) => {
            const st = press.current;
            const t = secAt(e.clientX);
            if (st === null) {
              setHover(t);
              onHover?.(t);
              return;
            }
            if (st.mode === 'seek') {
              props.onSeek(Math.max(0, t));
              return;
            }
            if (!st.dragging && Math.abs(e.clientX - st.startX) < DRAG_THRESHOLD) return;
            if (!st.dragging) setDragging(true);
            st.dragging = true;
            st.lastDelta = deltaFor(e.clientX, st);
            props.onDrag(st.lastDelta, 'move');
          }}
          onPointerUp={(e) => {
            const st = press.current;
            if (st === null) return;
            if (st.mode === 'grid' && !st.dragging) {
              press.current = null;
              const t = secAt(e.clientX);
              props.onBeatZero(e.altKey ? t : snapToOnset(props.onsets, t));
              return;
            }
            if (st.mode === 'grid') st.lastDelta = deltaFor(e.clientX, st);
            endPress(false);
          }}
          onPointerCancel={() => {
            endPress(true);
          }}
          onLostPointerCapture={() => {
            if (press.current !== null) endPress(press.current.mode === 'grid');
          }}
          onPointerLeave={() => {
            if (press.current === null) {
              setHover(null);
              onHover?.(null);
            }
          }}
        />
        {hoverText !== null && <span className={s.readout}>{hoverText}</span>}
      </div>
    </div>
  );
}
